import { Card, Stack, Typography, useMediaQuery } from '@mui/material';
import { useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import { Link, useNavigate } from 'react-router-dom';

import Button from '../../../components/styledComponents/Button';
import { DeleteButton } from '../../../components/styledComponents/IconButtons';
import colors from '../../../constants/colors';
import { PaymentAmountType } from '../../../constants/enums';
import { usePaymentsContext } from '../../../store/payments';
import { removePayment } from '../../../store/paymentsReducer';
import theme from '../../../styles/theme';

export const getPolicyString = (policyType: string) => {
  return `${policyType} policy`;
};

const PaymentShoppingCart = () => {
  const navigate = useNavigate();
  const { dispatch, state: paymentState } = usePaymentsContext();

  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('desktop'));

  const validPayments = Object.keys(paymentState.selectedPayments).filter(
    (policyNumber) =>
      paymentState.selectedPayments[policyNumber]?.paymentType !==
        PaymentAmountType.NO_PAYMENT &&
      paymentState.selectedPayments[policyNumber]?.amount &&
      parseFloat(paymentState.selectedPayments[policyNumber]?.amount ?? '') > 0
  );

  const paymentTotal = useMemo(
    () =>
      validPayments
        .reduce(
          (acc, policyNum) =>
            acc +
            parseFloat(paymentState.selectedPayments[policyNum]?.amount ?? ''),
          0
        )
        .toFixed(2),
    [validPayments]
  );

  const deletePayment = (policyNum: string): void => {
    dispatch(removePayment(policyNum));
  };

  return (
    <Card
      component={Stack}
      spacing={3}
      sx={{
        background: '#FFFFFF',
        borderRadius: 2,
        p: 2,
        height: 'fit-content',
        position: 'sticky',
        top: 0,
        minWidth: { mobile: 288, tablet: 300 },
        maxWidth: { mobile: 1, desktop: 358 },
        width: 1,
      }}
    >
      {!isMobile && (
        <Typography
          sx={{
            fontFamily: 'Cabin',
            fontSize: { tablet: 16, desktop: 28 },
            fontWeight: { mobile: 700 },
            mb: { tablet: 2, desktop: 3 },
          }}
        >
          {isMobileOrTablet ? 'Payment amount' : 'Pay multiple policies'}
        </Typography>
      )}
      {validPayments.length > 0 && (
        <Stack
          direction="column"
          spacing={2}
          sx={{
            borderColor: colors.csaaBorder,
            borderStyle: 'solid',
            borderRadius: 2,
            borderWidth: {
              mobile: 1,
            },
            p: {
              mobile: 2,
            },
            mb: { mobile: 3 },
          }}
        >
          {validPayments.map((policyNum, idx, arr) => (
            <Stack
              direction="row"
              key={policyNum}
              sx={{
                justifyContent: "space-between",
                alignItems: "flex-start",

                ...(arr.length > 1 &&
                  idx !== arr.length - 1 && {
                    borderBottom: `1px solid ${colors.csaaBorder}`,
                    pb: 2,
                  })
              }}>
              <Stack direction="column">
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  {getPolicyString(
                    paymentState.selectedPayments[policyNum].policyType
                  )}
                </Typography>
                <Typography sx={{ fontSize: 18 }}>{policyNum}</Typography>
              </Stack>
              <Stack direction="row" spacing={0.5} sx={{
                alignItems: "center"
              }}>
                <Typography
                  sx={{
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                    fontWeight: 700,
                    fontSize: 18,
                  }}
                >
                  <NumericFormat
                    value={paymentState.selectedPayments[policyNum].amount || 0}
                    displayType="text"
                    thousandSeparator
                    prefix="$"
                    fixedDecimalScale
                    decimalScale={2}
                  />
                </Typography>
                <DeleteButton
                  aria-label={`Remove payment of ${paymentState.selectedPayments[policyNum].amount} for policy ${policyNum}`}
                  onClick={() => deletePayment(policyNum)}
                />
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          borderRadius: 2,
          background: colors.csaaNeutralBackground,
          p: 1.5
        }}>
        <Typography
          sx={{
            fontSize: 28,
          }}
        >
          Total
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 28,
          }}
        >
          <NumericFormat
            value={paymentTotal || 0}
            displayType="text"
            thousandSeparator
            prefix="$"
            fixedDecimalScale
            decimalScale={2}
          />
        </Typography>
      </Stack>
      {validPayments.length > 0 && (
        <Stack
          direction={{ mobile: 'column', tablet: 'row' }}
          spacing={2}
          sx={{
            alignSelf: { tablet: 'flex-end', desktop: 'initial' }
          }}
        >
          {isMobileOrTablet && (
            <Button
              component={Link}
              relative="path"
              to=".."
              data-testid="payment-continue-button"
              variant="outlined"
              sx={{
                borderWidth: 2,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: 18,
                px: { mobile: 4, desktop: 3 },
                mt: { mobile: 5 },
                wordWrap: 'nowrap',
                color: colors.csaaBlack,
                borderColor: colors.csaaBlack,
                whiteSpace: 'nowrap',
              }}
              onClick={() => {
                navigate(-1);
              }}
            >
              Cancel
            </Button>
          )}
          <Button
            component={Link}
            to="/payment/method"
            data-testid="payment-continue-button"
            fullWidth
          >
            Continue
          </Button>
        </Stack>
      )}
    </Card>
  );
};

export default PaymentShoppingCart;
